import { initializeStores, resetStores } from "@/stores";
import { ServiceContainer } from "@/services/ServiceContainer";
import { logger } from "@/utils/logger";
import { identifyCreator, getCreatorId } from "@/utils/creatorUtils";

export interface InitializationConfig {
  width: number;
  height: number;
  aoEnabled: boolean;
  isMobile: boolean;
  ssaa?: boolean;
}

export interface InitializationStatus {
  isInitialized: boolean;
  errors: string[];
  warnings: string[];
  canRender: boolean;
  currentStep: string;
}

export interface InitializationResult {
  managerContainer: ServiceContainer;
  status: InitializationStatus;
}

export class AppInitializer {
  private static instance: AppInitializer;
  private isInitializing = false;
  private initializationPromise: Promise<InitializationResult> | null = null;
  private managerContainer: ServiceContainer | null = null;

  private constructor() {}

  static getInstance(): AppInitializer {
    if (!AppInitializer.instance) {
      AppInitializer.instance = new AppInitializer();
    }
    return AppInitializer.instance;
  }

  async initialize(
    config: InitializationConfig
  ): Promise<InitializationResult> {
    // Return existing promise if already initializing
    if (this.initializationPromise) {
      return this.initializationPromise;
    }

    if (this.isInitializing) {
      throw new Error("Initialization already in progress");
    }

    this.isInitializing = true;

    try {
      this.initializationPromise = this.performInitialization(config);
      const result = await this.initializationPromise;
      return result;
    } finally {
      this.isInitializing = false;
      this.initializationPromise = null;
    }
  }

  private async performInitialization(
    config: InitializationConfig
  ): Promise<InitializationResult> {
    const status: InitializationStatus = {
      isInitialized: false,
      errors: [],
      warnings: [],
      canRender: false,
      currentStep: "Starting initialization..."
    };

    try {
      // Step 1: Initialize Zustand stores
      status.currentStep = "Initializing application stores...";

      const managerContainer = new ServiceContainer();

      // Step 2: Initialize core stores
      status.currentStep = "Initializing core stores...";

      try {
        // Initialize all Zustand stores
        const storeInitialized = await initializeStores();

        if (!storeInitialized) {
          throw new Error("Store initialization failed");
        }

        logger.info(
          "All core stores initialized successfully",
          null,
          "AppInitializer"
        );
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Core stores failed to initialize";
        status.errors.push(errorMessage);
        logger.error(
          "Core stores initialization failed",
          error,
          "AppInitializer"
        );
        throw new Error(errorMessage);
      }

      // Step 3: Initialize manager container
      status.currentStep = "Initializing 3D services...";

      const managerStatus = await managerContainer.initialize(config);
      if (!managerStatus.isInitialized) {
        throw new Error("3D services failed to initialize");
      }

      // Step 4: Load creator data
      status.currentStep = "Loading creator data...";

      const creatorId = identifyCreator() || getCreatorId();
      if (creatorId) {
        try {
          // Import the creator store hook to load creator
          const { useCreatorStore, useColorStore } = await import("@/stores");
          await useCreatorStore.getState().loadCreator(creatorId);
          logger.info(
            "Creator data loaded successfully",
            { creatorId },
            "AppInitializer"
          );
          // Import the color store hook to load presets
          await useColorStore.getState().fetchPresets();
          // Step 4.5: Initialize theme after creator is loaded
          try {
            const { initializeTheme } = await import("@/utils/themeUtils");
            await initializeTheme();
          } catch (themeError) {
            logger.warn(
              "Failed to initialize theme",
              themeError,
              "AppInitializer"
            );
            status.warnings.push("Theme could not be initialized");
          }

          try {
            useCreatorStore.getState().buildCreatorDataHierarchy();
            logger.info("Builder data loaded successfully", "AppInitializer");
          } catch (error) {
            logger.warn(
              "Failed to build creator data hierarchy",
              error,
              "AppInitializer"
            );
            status.warnings.push("Creator data hierarchy could not be built");
          }
        } catch (error) {
          logger.warn("Failed to load creator data", error, "AppInitializer");
          status.warnings.push("Creator data could not be loaded");
        }
      }

      // Step 5: Finalize initialization
      status.currentStep = "Finalizing initialization...";
      status.isInitialized = true;
      status.canRender = true;

      logger.info(
        "Application initialization completed successfully",
        null,
        "AppInitializer"
      );

      this.managerContainer = managerContainer;
      return {
        managerContainer,
        status
      };
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Unknown initialization error";
      status.errors.push(errorMessage);
      status.canRender = false;

      logger.error(
        "Application initialization failed",
        error,
        "AppInitializer"
      );

      throw new Error(`Initialization failed: ${errorMessage}`);
    }
  }

  reset(): void {
    this.isInitializing = false;
    this.initializationPromise = null;
    resetStores();
    this.managerContainer = null;
  }

  getInitializationStatus(): { isInitializing: boolean } {
    return { isInitializing: this.isInitializing };
  }

  getServiceContainer(): ServiceContainer | null {
    return this.managerContainer;
  }
}
